import { useGLTF } from '@react-three/drei'
import { useEffect, useMemo } from 'react'
import type { GLTFLoader } from 'three-stdlib'
import GLTFMaterialsVariantsExtension from '../KHR_materials_variants'
import {
    createOwnedGLBInstance,
} from '../GLB/createOwnedGLBInstance'
import {
    releaseOwnedGLBInstance,
    retainOwnedGLBInstance,
} from '../GLB/ownedGLBInstanceLifecycle'
import type {
    GLBModel,
    GLTFSource,
} from '../GLB/types'

export {
    type HasStandardMaterial,
} from '../GLB/types'

const configureModelLoader = (loader:GLTFLoader) => {
    loader.register(
        parser => new GLTFMaterialsVariantsExtension(parser),
    )
}

export const PreloadGLB = (url:URL):void => {
    useGLTF.preload(
        url.href,
        undefined,
        undefined,
        configureModelLoader,
    )
}

/**
 * Drei owns one immutable decoded GLTF per URL. This hook unconditionally
 * reads that cache, then creates the mutable scene/material state belonging to
 * this mounted viewer instance.
 */
export default function useGLB(url:URL):GLBModel {
    const source = useGLTF(
        url.href,
        undefined,
        undefined,
        configureModelLoader,
    ) as GLTFSource
    const instance = useMemo(
        () => createOwnedGLBInstance(source),
        [source],
    )

    useEffect(() => {
        retainOwnedGLBInstance(instance)
        return () => releaseOwnedGLBInstance(instance)
    }, [instance])

    return instance.model
}
